import { ArrowRight, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { BRAND_LOGO_SQUARE } from '../brandAssets';
import { USERNAME_REGEX } from '../constants';
import { UsernameInput } from './UsernameInput';
import { Wordmark } from './Wordmark';

interface AuthScreenProps {
  onLogin: (email: string, pass: string) => Promise<void>;
  onRegister: (email: string, pass: string, displayName: string, username: string) => Promise<void>;
}

export const AuthScreen: React.FC<AuthScreenProps> = ({ onLogin, onRegister }) => {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot-password'>('login');
  
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (mode === 'login') {
        await onLogin(email, password);
      } else if (mode === 'register') {

        if (!displayName.trim()) throw new Error("Display Name is required");
        if (!username.trim()) throw new Error("Username is required");
        
        // Username validation: specific regex
        if (!USERNAME_REGEX.test(username)) {
            throw new Error("Username can only contain lowercase letters, numbers, and underscores.");
        }

        if (password.length < 6) throw new Error("Password must be at least 6 characters");
        if (password !== confirmPassword) throw new Error("Passwords do not match");
        await onRegister(email, password, displayName, username);
      } else if (mode === 'forgot-password') {
        const { db } = await import('../services/db');
        await db.resetPasswordForEmail(email);
        setError("If an account exists, a password reset email has been sent.");
        setLoading(false);
        return; // Don't clear loading/error in finally block if we want to show success message
      }
    } catch (err: any) {
      console.error(err);
      if (err.message.includes('Username is already taken') || err.message.includes('unique constraint') || err.code === '23505') {
          setError("That username is already taken. Try another!");
      } else if (err.message.includes('User already registered')) {
          setError("Email already in use. Try logging in.");
      } else if (err.message.includes('Username can only contain')) {
          setError(err.message);
      } else {
          setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
    setConfirmPassword('');
  };

  const toggleForgotPassword = () => {
    setMode(mode === 'forgot-password' ? 'login' : 'forgot-password');
    setError('');
  };

  return (
    <div className="min-h-screen bg-brand-cream relative flex flex-col items-center justify-center p-6 overflow-hidden">
      {/* Texture Background */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/crissxcross.png")' }}></div>
      
      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
            <div className="relative mb-6 animate-stamp">
                <img 
                    src={BRAND_LOGO_SQUARE} 
                    alt="Good Shout logo" 
                    className="relative w-40 h-40 rounded-full shadow-2xl" 
                />
            </div>

            <h1 className="animate-text-enter" style={{ animationDelay: '0.6s' }}><Wordmark size="lg" /></h1>
            <p className="text-brand-dark-light font-medium text-sm mt-2 text-center animate-text-enter" style={{ animationDelay: '0.8s' }}>Trust your friends, not the bots.</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 relative overflow-hidden border border-brand-cream-dark">
            <h2 className="font-display font-extrabold text-2xl text-brand-dark mb-6">
                {mode === 'login' ? 'Welcome Back' : mode === 'register' ? 'Join the Club' : 'Reset Password'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
                {mode === 'register' && (
                     <div className="animate-in slide-in-from-left-4 fade-in">
                        <label className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Display Name</label>
                        <input 
                            type="text" 
                            value={displayName}
                            onChange={e => setDisplayName(e.target.value)}
                            className="w-full p-3 border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red font-semibold text-brand-dark bg-brand-cream"
                            placeholder="e.g. John Doe"
                        />
                    </div>
                )}

                {mode === 'register' && (
                    <div className="animate-in slide-in-from-left-4 fade-in">
                        <label className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Username</label>
                        <UsernameInput 
                            value={username}
                            onChange={val => {
                                setUsername(val);
                                if (error) setError('');
                            }}
                            className="p-3 border-2 border-brand-cream-dark rounded-xl focus-within:border-brand-red font-semibold text-brand-dark bg-brand-cream"
                        />
                    </div>
                )}

                <div>
                    <label htmlFor="email" className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Email</label>
                    <input 
                        id="email"
                        type="email" 
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full p-3 border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red font-semibold text-brand-dark bg-brand-cream"
                        placeholder="e.g. charlie@example.com"
                    />
                </div>
                
                {mode !== 'forgot-password' && (
                    <div>
                        <label htmlFor="password" className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Password</label>
                        <input 
                            id="password"
                            type="password" 
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            className={`w-full p-3 border-2 rounded-xl outline-none font-semibold text-brand-dark bg-brand-cream ${
                                mode === 'register' && confirmPassword && password !== confirmPassword 
                                    ? 'border-brand-red focus:border-brand-red' 
                                    : 'border-brand-cream-dark focus:border-brand-red'
                            }`}
                            placeholder="••••••••"
                        />
                    </div>
                )}

                {mode === 'register' && (
                    <div className="animate-in slide-in-from-left-4 fade-in">
                        <label htmlFor="confirm-password" className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Confirm Password</label>
                        <input 
                            id="confirm-password"
                            type="password" 
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            className={`w-full p-3 border-2 rounded-xl outline-none font-semibold text-brand-dark bg-brand-cream ${
                                password !== confirmPassword 
                                    ? 'border-brand-red focus:border-brand-red' 
                                    : 'border-brand-cream-dark focus:border-brand-red'
                            }`}
                            placeholder="••••••••"
                        />
                    </div>
                )}

                {error && (
                    <p className="text-brand-red text-sm font-bold text-center bg-brand-red/10 p-3 rounded-lg animate-in fade-in">{error}</p>
                )}

                <button 
                    type="submit"
                    disabled={loading || !email || (mode !== 'forgot-password' && !password) || (mode === 'register' && (!username || !displayName || !confirmPassword))}
                    className="w-full py-4 bg-brand-red text-white rounded-xl font-bold shadow-lg hover:bg-brand-red-hover transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                >
                    {loading ? <Loader2 className="animate-spin" /> : (
                        mode === 'login' ? 'Log In' : mode === 'register' ? 'Sign Up' : 'Send Reset Link'
                    )}
                    {!loading && mode === 'register' && <ArrowRight size={18} />}
                </button>
            </form>

            <div className="mt-6 pt-6 border-t border-brand-cream-dark text-center relative z-10">
                <button 
                    type="button"
                    onClick={toggleMode}
                    className="text-sm font-bold text-brand-dark-light hover:text-brand-red transition"
                >
                    {mode === 'login' ? "Don't have an account? Sign up" : mode === 'register' ? "Already have an account? Log in" : "Back to Login"}
                </button>
                
                {mode === 'login' && (
                    <button 
                        type="button"
                        onClick={toggleForgotPassword}
                        className="block w-full mt-4 text-xs font-bold text-brand-dark-light hover:text-brand-red transition tracking-wide"
                    >
                        Forgot Password?
                    </button>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};
